import {
  AdminTypeOptionDemo,
  AndOrFilterDemo,
  ChipColorDemo,
  CircleOptionDemo,
  ClearFiltersButtonDemo,
  ContainedButtonDemo,
  CustomTooltipBorderDemo,
  CustomTooltipDemo,
  ExternalLinkDemo,
  FilterMenuDemo,
  FilterSubmenuDemo,
  FormTextFieldDemo,
  IconButtonDemo,
  IconChipDemo,
  IconContainerDemo,
  LoadingIconDemo,
  OutlinedButtonDemo,
  SearchAndOrFilterDemo,
  SearchFilterDemo,
  SearchInputDemo,
  SeverityOptionDemo,
  SwitchFilterDemo,
  TagInputDemo,
  TextButtonDemo,
  TimeFilterDemo,
  ToastDemo,
  ToggleGroupDemo,
  ToggleSwitchDemo,
  TooltipWrapperDemo,
} from "./demos";

// Sidebar Components

// SVG Icons
import IconGalleryDemo from "./demos/IconGalleryDemo";
import SoftButtonDemo from "./demos/SoftButtonDemo";
import SvgToIconDemo from "./demos/SvgToIconDemo";

/**
 * Documentation data for all shared components
 * Each object represents a component with its props, example code, and demo
 */
const componentsData = [
  {
    id: "custom-tooltip",
    name: "CustomTooltip",
    description: "A styled tooltip component with custom appearance.",
    props: [
      { name: "title", type: "node", description: "Tooltip content" },
      {
        name: "children",
        type: "node",
        description: "The element that triggers the tooltip",
      },
      {
        name: "style",
        type: "object",
        description: "Additional styles for the tooltip",
        defaultValue: "null",
      },
    ],
    example: `import CustomTooltip from '../components/CustomTooltip';
import { Button, alpha, useTheme } from "@mui/material";

const theme = useTheme();

// Basic tooltip
<CustomTooltip title="This is a tooltip">
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Hover me
  </Button>
</CustomTooltip>

// With custom style
<CustomTooltip 
  title="Custom styled tooltip" 
  style={{ backgroundColor: alpha(theme.palette.primary.light, 0.1) }}
>
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Custom Style
  </Button>
</CustomTooltip>`,
    demo: CustomTooltipDemo,
    category: "Display",
  },
  {
    id: "loading-icon",
    name: "LoadingIcon",
    description:
      "A loading indicator component that supports different styles.",
    props: [
      {
        name: "type",
        type: "string",
        description: 'Type of loading indicator: "circular" or "dots"',
        defaultValue: '"circular"',
      },
      {
        name: "props",
        type: "object",
        description: "Additional props passed to the underlying component",
      },
    ],
    example: `import LoadingIcon from '../components/LoadingIcon';

// Default
<LoadingIcon />

// Dots
<LoadingIcon type="dots" />

// Custom Size
<LoadingIcon size={30} />`,
    demo: LoadingIconDemo,
    category: "Feedback",
  },
  {
    id: "tooltip-wrapper",
    name: "TooltipWrapper",
    description: "A tooltip wrapper component with additional features.",
    props: [
      {
        name: "name",
        type: "string",
        description: "Unique name for the tooltip",
      },
      {
        name: "content",
        type: "node",
        description: "Content to display in the tooltip",
      },
      {
        name: "children",
        type: "node",
        description: "Element that triggers the tooltip",
      },
      {
        name: "style",
        type: "object",
        description: "Additional styles for the tooltip",
        defaultValue: "{}",
      },
    ],
    example: `import TooltipWrapper from '../components/TooltipWrapper';
import { Box, Button, Typography, useTheme } from "@mui/material";

const theme = useTheme();

<TooltipWrapper
  name="example-tooltip"
  content={
    <Box p={1}>
      <Typography variant="subtitle2">Detailed Information</Typography>
      <Typography variant="body2">
        This is a more complex tooltip with formatted content.
      </Typography>
    </Box>
  }
  style={{ borderTop: \`2px solid \${theme.palette.primary.main}\` }}
>
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Hover for details
  </Button>
</TooltipWrapper>`,
    demo: TooltipWrapperDemo,
    category: "Display",
  },
  {
    id: "custom-tooltip-border",
    name: "CustomTooltipBorder",
    description:
      "A tooltip component with customizable border color and placement.",
    props: [
      {
        name: "title",
        type: "node",
        description: "Content to display in the tooltip",
      },
      {
        name: "children",
        type: "node",
        description: "Element that triggers the tooltip",
      },
      {
        name: "borderColor",
        type: "string",
        description: "Color of the top border of the tooltip",
        defaultValue: "theme.palette.info.main",
      },
      {
        name: "placement",
        type: "string",
        description: "Placement of the tooltip relative to the children",
        defaultValue: '"top"',
      },
    ],
    example: `import CustomTooltipBorder from '../components/CustomTooltipBorder';
import { Box, Button, Typography, useTheme } from "@mui/material";

const theme = useTheme();

// Default border color
<CustomTooltipBorder
  title={
    <Box p={1}>
      <Typography variant="caption" color="inherit">
        Default border color
      </Typography>
    </Box>
  }
>
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Default Border
  </Button>
</CustomTooltipBorder>

// Custom border color
<CustomTooltipBorder
  title={
    <Box p={1}>
      <Typography variant="caption" color="inherit">
        Custom border color
      </Typography>
    </Box>
  }
  borderColor={theme.palette.error.main}
>
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Red Border
  </Button>
</CustomTooltipBorder>

// Bottom placement
<CustomTooltipBorder
  title={
    <Box p={1}>
      <Typography variant="caption" color="inherit">
        Bottom placement
      </Typography>
    </Box>
  }
  placement="bottom"
  borderColor={theme.palette.success.main}
>
  <Button variant="outlined" size="small" sx={{ borderRadius: 2 }}>
    Bottom Placement
  </Button>
</CustomTooltipBorder>

// Tooltip on a disabled button (wrap in span)
// Tooltips do not work on disabled elements directly, so wrap the button in a <span>
<CustomTooltipBorder title={<span>Tooltip for disabled</span>}>
  <span>
    <Button variant="outlined" size="small" sx={{ borderRadius: 2 }} disabled>
      Disabled Button
    </Button>
  </span>
</CustomTooltipBorder>`,
    demo: CustomTooltipBorderDemo,
    category: "Display",
  },
  {
    id: "toggle-switch",
    name: "ToggleSwitch",
    description:
      "A toggle switch component that handles API updates and loading states.",
    props: [
      {
        name: "defaultValue",
        type: "boolean",
        description: "Initial state of the toggle",
      },
      {
        name: "updateFunction",
        type: "function",
        description:
          "Function that gets called when the toggle changes, should return a Promise",
      },
      {
        name: "setFetchStatus",
        type: "function",
        description: "Function to update the loading/success/error status",
      },
      {
        name: "isPermanentlyDisabled",
        type: "boolean",
        description: "Whether the toggle should be permanently disabled",
        defaultValue: "false",
      },
    ],
    example: `import ToggleSwitch from '../components/ToggleSwitch';
import { operationStatus } from '../components/ActionStatus';
import { useState } from "react";

const [fetchStatus, setFetchStatus] = useState(operationStatus.IDLE);

// Mock update function that simulates an API call
const mockUpdateFunction = (newValue) => {
  return new Promise((resolve) => {
    // Simulate API delay
    setTimeout(() => {
      resolve();
    }, 1000);
  });
};

// Active Toggle
<ToggleSwitch
  defaultValue={true}
  updateFunction={mockUpdateFunction}
  setFetchStatus={setFetchStatus}
/>

// Inactive Toggle
<ToggleSwitch
  defaultValue={false}
  updateFunction={mockUpdateFunction}
  setFetchStatus={setFetchStatus}
/>

// Disabled Toggle
<ToggleSwitch
  defaultValue={true}
  updateFunction={mockUpdateFunction}
  setFetchStatus={setFetchStatus}
  isPermanentlyDisabled={true}
/>`,
    demo: ToggleSwitchDemo,
    category: "Form Controls",
  },
  {
    id: "form-text-field",
    name: "FormTextField",
    description:
      "A styled text field component integrated with Formik for form management. Automatically handles validation, error states, and field updates.",
    props: [
      {
        name: "label",
        type: "string",
        description: "The label text displayed for the text field",
      },
      {
        name: "fieldName",
        type: "string",
        description:
          "The name of the field in the Formik form (must match Formik field name)",
      },
      {
        name: "emptyOnChange",
        type: "boolean",
        description:
          "If true, prevents onChange from being triggered. Useful when integrating with Autocomplete components that handle their own onChange",
        defaultValue: "false",
      },
      {
        name: "helperText",
        type: "string",
        description:
          "Helper text displayed below the field (when not in error state)",
        defaultValue: '""',
      },
      {
        name: "required",
        type: "boolean",
        description: "Marks the field as required (displays asterisk)",
        defaultValue: "false",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Disables the text field",
        defaultValue: "false",
      },
      {
        name: "type",
        type: "string",
        description: "Input type (e.g., 'text', 'email', 'password')",
        defaultValue: '"text"',
      },
      {
        name: "...props",
        type: "object",
        description:
          "Additional props passed to the underlying StyledTextField component",
      },
    ],
    example: `import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormTextField from "../components/forms/FormTextField";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(3, "Name must be at least 3 characters"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

// Basic usage
<Formik
  initialValues={{ name: "", email: "" }}
  validationSchema={validationSchema}
  onSubmit={(values) => console.log(values)}
>
  <Form>
    <FormTextField
      label="Name"
      fieldName="name"
      required
    />
    <FormTextField
      label="Email"
      fieldName="email"
      type="email"
      required
    />
  </Form>
</Formik>

// With helper text
<FormTextField
  label="Username"
  fieldName="username"
  helperText="Choose a unique username"
/>

// With emptyOnChange (for Autocomplete integration)
<Autocomplete
  options={options}
  renderInput={(params) => (
    <FormTextField
      {...params}
      label="Select option"
      fieldName="selectedOption"
      emptyOnChange
    />
  )}
/>`,
    demo: FormTextFieldDemo,
    category: "Form Controls",
  },
  {
    id: "external-link",
    name: "TextWithExternalLink",
    description:
      "A component that displays text with an external link and icon.",
    props: [
      {
        name: "text",
        type: "string",
        description: "Text to display before the link",
      },
      {
        name: "linkText",
        type: "string",
        description: "Text for the clickable link",
      },
      {
        name: "href",
        type: "string",
        description: "URL for the link to navigate to",
      },
      {
        name: "tooltipText",
        type: "string",
        description: "text for the tooltip",
        defaultValue: "View in new tab",
      },
      {
        name: "linkColor",
        type: "string",
        description: "color for the link",
        defaultValue: "colors.primary_main",
      },
    ],
    example: `import TextWithExternalLink from '../components/ExternalLink';

// Platform link
<TextWithExternalLink
  text="Learn more about our platform at"
  linkText="our website"
  href="https://example.com"
/>

// Documentation link
<TextWithExternalLink
  text="Check out our documentation at"
  linkText="docs.example.com"
  tooltipText="Some tooltip text for this specific link!"
  href="https://docs.example.com"
/>

// Support link
<TextWithExternalLink
  text="For support, visit"
  linkText="support portal"
  href="https://support.example.com"
  linkColor={"red"}
/>`,
    demo: ExternalLinkDemo,
    category: "Navigation",
  },
  {
    id: "circle-option",
    name: "CircleOption",
    description:
      "A component that displays a colored circle with a label, used for status indicators in filters.",
    props: [
      {
        name: "label",
        type: "string",
        description: "The text label to display next to the circle",
      },
      {
        name: "type",
        type: "string",
        description:
          "The type of circle option which determines the color ('status', 'priority', 'feasibility', 'impact', 'jiraStatus')",
      },
    ],
    example: `import CircleOption from '../components/filterOption/CircleOption';

// Status type
<CircleOption label="Active" type="status" />

// Priority type
<CircleOption label="High" type="priority" />

// Feasibility type
<CircleOption label="Easy" type="feasibility" />

// Jira status type
<CircleOption label="In Progress" type="jiraStatus" />`,
    demo: CircleOptionDemo,
    category: "Display",
  },
  {
    id: "severity-option",
    name: "SeverityOption",
    description:
      "A component that displays an icon with a label, used for severity or priority indicators in filters.",
    props: [
      {
        name: "label",
        type: "string",
        description: "The text label to display next to the icon",
      },
      {
        name: "type",
        type: "string",
        description:
          "The type of severity option which determines the icon ('severity', 'jira', 'entityRisk', 'tenantRisk')",
      },
    ],
    example: `import SeverityOption from '../components/filterOption/SeverityOption';

// Severity type
<SeverityOption label="Critical" type="severity" />

// Jira type
<SeverityOption label="Highest" type="jira" />

// Entity Risk type
<SeverityOption label="High" type="entityRisk" />

// Tenant Risk type
<SeverityOption label="Medium" type="tenantRisk" />`,
    demo: SeverityOptionDemo,
    category: "Display",
  },
  {
    id: "admin-type-option",
    name: "AdminTypeOption",
    description:
      "A component that displays an icon with an admin type label, used for permission filters.",
    props: [
      {
        name: "adminType",
        type: "string",
        description:
          "The admin type to display (e.g., 'Admin', 'Editor', 'Viewer')",
      },
      {
        name: "category",
        type: "string",
        description:
          "The category of admin permission ('user', 'system', etc.)",
      },
    ],
    example: `import AdminTypeOption from '../components/filterOption/AdminTypeOption';

// User Admin Types
<AdminTypeOption adminType="Admin" category="user" />
<AdminTypeOption adminType="Editor" category="user" />
<AdminTypeOption adminType="Viewer" category="user" />

// System Admin Types
<AdminTypeOption adminType="System Admin" category="system" />
<AdminTypeOption adminType="System Operator" category="system" />`,
    demo: AdminTypeOptionDemo,
    category: "Display",
  },
  {
    id: "filter-menu",
    name: "FilterMenu",
    description:
      "A dropdown filter menu component for selecting multiple filter options.",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The name of the filter displayed in the button",
      },
      {
        name: "fieldName",
        type: "string",
        description: "The field name used in the appliedFilters object",
      },
      {
        name: "options",
        type: "array",
        description:
          "Array of option objects with name, label, and displayName properties",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "Object containing all currently applied filters",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the applied filters",
      },
      {
        name: "fullOptions",
        type: "array",
        description: "Array of all available options for this filter type",
      },
    ],
    example: `import FilterMenu from '../components/filters/FilterMenu';
import CircleOption from '../components/filterOption/CircleOption';

// Define filter options with display components
const statusOptions = ["Gap", "Compliant", "Snoozed", "Excluded"].map(status => ({
  name: status,
  label: <CircleOption label={status} type="status" />,
  displayName: status
}));

// In your component
const [appliedFilters, setAppliedFilters] = useState({
  status: {}
});

// Render the filter menu
<FilterMenu
  filterName="Status"
  fieldName="status"
  options={statusOptions}
  appliedFilters={appliedFilters}
  setAppliedFilters={setAppliedFilters}
  fullOptions={["Gap", "Compliant", "Snoozed", "Excluded"]}
/>`,
    demo: FilterMenuDemo,
    category: "Filters",
  },
  {
    id: "search-filter",
    name: "SearchFilter",
    description:
      "An enhanced filter menu with search functionality for filtering long option lists.",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The name of the filter displayed in the button",
      },
      {
        name: "fieldName",
        type: "string",
        description: "The field name used in the appliedFilters object",
      },
      {
        name: "options",
        type: "array",
        description:
          "Array of option objects with name, label, and displayName properties",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "Object containing all currently applied filters",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the applied filters",
      },
      {
        name: "fullOptions",
        type: "array",
        description: "Array of all available options for this filter type",
      },
    ],
    example: `import SearchFilter from '../components/filters/SearchFilter';
import IntegrationRowFilter from '../scenes/posture/steps/components/IntegrationRowFilter';

// Define filter options with integration icons
const toolOptions = ["aws", "okta", "azure", "github"].map(tool => ({
  name: tool,
  label: <IntegrationRowFilter name={tool} />,
  displayName: tool
}));

// In your component
const [appliedFilters, setAppliedFilters] = useState({
  tools: {}
});

// Render the search filter
<SearchFilter
  filterName="Tool"
  fieldName="tools"
  options={toolOptions}
  appliedFilters={appliedFilters}
  setAppliedFilters={setAppliedFilters}
  fullOptions={["aws", "okta", "azure", "github"]}
/>`,
    demo: SearchFilterDemo,
    category: "Filters",
  },
  {
    id: "search-input",
    name: "SearchInput",
    description: "A search input component for filtering content by text.",
    props: [
      {
        name: "fieldName",
        type: "string",
        description:
          "The field name used in the appliedFilters object (typically 'searchString')",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "Object containing all currently applied filters",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the applied filters",
      },
      {
        name: "minSearchLength",
        type: "number",
        description:
          "Minimum number of characters required before search is applied",
        defaultValue: "1",
      },
    ],
    example: `import SearchInput from '../components/filters/SearchInput';

// In your component
const [appliedFilters, setAppliedFilters] = useState({
  searchString: ""
});

// Render the search input
<SearchInput
  fieldName="searchString"
  appliedFilters={appliedFilters}
  setAppliedFilters={setAppliedFilters}
/>

// Later, use the search string to filter your data
const filteredData = data.filter(item => 
  item.name.toLowerCase().includes(appliedFilters.searchString.toLowerCase())
);`,
    demo: SearchInputDemo,
    category: "Filters",
  },
  {
    id: "clear-filters-button",
    name: "ClearFiltersButton",
    description:
      "A button that clears all applied filters with a single click.",
    props: [
      {
        name: "appliedFilters",
        type: "object",
        description: "Object containing all currently applied filters",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the applied filters",
      },
    ],
    example: `import { Box } from '@mui/material';
    import FilterMenu from '../components/filters/FilterMenu';
    import SearchInput from '../components/filters/SearchInput';
    import ClearFiltersButton from '../components/filters/ClearFiltersButton';
    import CircleOption from '../components/filterOption/CircleOption';

    // In your component
    const [appliedFilters, setAppliedFilters] = useState({
      status: {},
      priority: {},
      searchString: ""
    });

    // Define filter options
    const statusOptions = ["Gap", "Compliant", "Snoozed", "Excluded"];
    const priorityOptions = ["Lowest", "Low", "Medium", "High", "Highest"];

    // Render a complete filter bar with multiple filter components
    <Box display="flex" flexDirection="row" gap={2} alignItems="center">
      {/* Status Filter */}
      <FilterMenu
        filterName="Status"
        fieldName="status"
        options={statusOptions.map(status => ({
          name: status,
          label: <CircleOption label={status} type="status" />,
          displayName: status
        }))}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        fullOptions={statusOptions}
      />
      
      {/* Priority Filter */}
      <FilterMenu
        filterName="Priority"
        fieldName="priority"
        options={priorityOptions.map(priority => ({
          name: priority,
          label: <CircleOption label={priority} type="priority" />,
          displayName: priority
        }))}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        fullOptions={priorityOptions}
      />
      
      {/* Search Input */}
      <SearchInput
        fieldName="searchString"
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
      
      {/* Clear Filters Button */}
      <ClearFiltersButton
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
      />
    </Box>`,
    demo: ClearFiltersButtonDemo,
    category: "Filters",
  },
  {
    id: "switch-filter",
    name: "SwitchFilter",
    description:
      "A labeled switch for filtering, with custom styling and label.",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The key in appliedFilters to control.",
      },
      {
        name: "label",
        type: "string",
        description: "The label to display next to the switch.",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "The current filters state object.",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the filters state.",
      },
    ],
    example: `import SwitchFilter from '../components/filters/SwitchFilter';
import { useState } from 'react';

const [appliedFilters, setAppliedFilters] = useState({ showActive: false });

<SwitchFilter
  filterName="showActive"
  label="Only Active Items"
  appliedFilters={appliedFilters}
  setAppliedFilters={setAppliedFilters}
/>`,
    demo: SwitchFilterDemo,
    category: "Filters",
  },

  // SVG Icons Components
  {
    id: "svg-to-icon",
    name: "SvgToIcon",
    category: "Icons",
    description:
      "A wrapper component that simplifies the usage of SVG icons with customizable sizing and styling",
    props: [
      {
        name: "IconLogo",
        type: "Component",
        description: "The SVG component to render",
        defaultValue: null,
      },
      {
        name: "width",
        type: "string",
        description: "Width of the icon",
        defaultValue: '"24px"',
      },
      {
        name: "height",
        type: "string",
        description: "Height of the icon",
        defaultValue: '"auto"',
      },
      {
        name: "style",
        type: "object",
        description: "Additional styles to apply to the icon",
        defaultValue: "{}",
      },
      {
        name: "children",
        type: "node",
        description: "Optional nested content inside the icon",
        defaultValue: "null",
      },
    ],
    example: `import SvgToIcon from "../icons/SvgToIcon";
import { Notifications as NotificationsIcon } from "@mui/icons-material";

// Basic usage with Material-UI icon
<SvgToIcon IconLogo={NotificationsIcon} />

// Custom size
<SvgToIcon 
  IconLogo={NotificationsIcon} 
  width="40px" 
  height="40px" 
/>

// Custom styling
<SvgToIcon 
  IconLogo={NotificationsIcon} 
  style={{ color: 'red', opacity: 0.8 }} 
/>`,
    demo: SvgToIconDemo,
  },
  // IconGallery Component
  {
    id: "icon-gallery",
    name: "Icon Gallery",
    category: "Icons",
    description:
      "A comprehensive gallery of all available icons in the application, with search and one-click copy functionality",
    props: [
      {
        name: "N/A",
        type: "N/A",
        description:
          "This is a standalone documentation component with no props",
        defaultValue: null,
      },
    ],
    example: `// The Icon Gallery is a full component.
    // To use an icon from the gallery in your code:
    // 1. Find the icon you need
    // 2. Click directly on the icon to copy its code
    // 3. Paste in your component

    import SvgToIcon from "../icons/SvgToIcon";
    import { icons, logos, severities } from "../scenes/stack/environment/icons/iconExport";

    // Examples:
    <SvgToIcon IconLogo={icons.userIcon} />
    <SvgToIcon IconLogo={logos.BreezeLogo} />
    <SvgToIcon IconLogo={severities.CriticalSeverityIcon} />`,
    demo: IconGalleryDemo,
  },
  {
    id: "toast",
    name: "Toast",
    description:
      "A notification component with different severity levels and customizable content",
    props: [
      {
        name: "text",
        type: "string",
        description: "The main message text to display in the toast",
      },
      {
        name: "content",
        type: "string",
        description: "Additional content text displayed below the main message",
        defaultValue: "null",
      },
      {
        name: "comment",
        type: "string",
        description: "Secondary comment text displayed in smaller font",
        defaultValue: "null",
      },
      {
        name: "recommended_action",
        type: "string",
        description: "Text for the primary action button",
        defaultValue: "null",
      },
      {
        name: "unrecommended_action",
        type: "string",
        description: "Text for the secondary action button",
        defaultValue: "null",
      },
      {
        name: "recommended_action_callback",
        type: "function",
        description: "Callback function for the primary action button",
        defaultValue: "null",
      },
      {
        name: "unrecommended_action_callback",
        type: "function",
        description: "Callback function for the secondary action button",
        defaultValue: "null",
      },
    ],
    example: `import { Toast } from '../components/Toast';

// Simple usage with string
Toast.success("Operation completed successfully");
Toast.info("New update is available");
Toast.warning("This action may have consequences");
Toast.error("An error occurred during the operation");

// Real-world examples from the app
Toast.success("Scan completed successfully");
Toast.info("Scenarios scan in progress...");
Toast.error("Scan failed");

// Integration examples with action buttons
Toast.success({
  text: "Asana task created successfully!",
  recommended_action: "View",
  recommended_action_callback: () => {
    window.open(taskUrl, "_blank");
  },
});

Toast.success({
  text: "Issue created successfully!",
  recommended_action: "View",
  recommended_action_callback: () => {
    window.open(url, "_blank");
  },
});

// Advanced usage with all options
Toast.success({
  text: "Operation completed successfully",
  content: "All resources were processed correctly",
  comment: "The system will update in the background",
  recommended_action: "View Details",
  unrecommended_action: "Dismiss",
  recommended_action_callback: () => console.log("View details clicked"),
  unrecommended_action_callback: () => console.log("Dismiss clicked")
});`,
    demo: ToastDemo,
    category: "Feedback",
  },
  {
    id: "chip-color",
    name: "ChipColor",
    description:
      "A customizable colored chip component for displaying different types of status and risk levels",
    props: [
      {
        name: "value",
        type: "string | number",
        description: "The value to display in the chip",
      },
      {
        name: "type",
        type: "string",
        description:
          "Chip type which determines the color scheme (e.g., 'entity_risk', 'tenant_risk', 'vulnerabilitySeverity')",
        defaultValue: "null",
      },
      {
        name: "width",
        type: "number",
        description: "Width of the chip in pixels",
        defaultValue: "90",
      },
      {
        name: "size",
        type: "string",
        description: "Size of the chip: 'small', 'medium', or 'large'",
        defaultValue: "'medium'",
      },
      {
        name: "keyMapping",
        type: "function",
        description:
          "Function to map the value to a key in the chip configuration",
        defaultValue: "null",
      },
      {
        name: "useValueAsLabel",
        type: "boolean",
        description:
          "Whether to use the value as the label instead of the configured label",
        defaultValue: "null",
      },
      {
        name: "useShortLabel",
        type: "boolean",
        description: "Whether to use the short label variant when available",
        defaultValue: "false",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the chip is in a disabled state",
        defaultValue: "false",
      },
      {
        name: "labelWidth",
        type: "boolean",
        description:
          "Whether to set the width based on the label instead of fixed width",
        defaultValue: "false",
      },
      {
        name: "defaultColor",
        type: "boolean",
        description:
          "Whether to use the default color instead of the type-based color",
        defaultValue: "false",
      },
      {
        name: "style",
        type: "object",
        description: "Additional inline styles to apply to the chip",
        defaultValue: "{}",
      },
    ],
    example: `import ChipColor from '../scenes/remediation/plan/components/ChipColor';

// Basic usage with entity risk
<ChipColor type="entity_risk" value={1} />  // Minimal Risk
<ChipColor type="entity_risk" value={2} />  // Low Risk
<ChipColor type="entity_risk" value={3} />  // Medium Risk
<ChipColor type="entity_risk" value={4} />  // High Risk
<ChipColor type="entity_risk" value={5} />  // Critical Risk

// Tenant risk examples
<ChipColor type="tenant_risk" value="Low" />
<ChipColor type="tenant_risk" value="Medium" />
<ChipColor type="tenant_risk" value="High" />
<ChipColor type="tenant_risk" value="Critical" />

// Vulnerability severity examples
<ChipColor type="vulnerabilitySeverity" value="LOW" />
<ChipColor type="vulnerabilitySeverity" value="MEDIUM" />
<ChipColor type="vulnerabilitySeverity" value="HIGH" />
<ChipColor type="vulnerabilitySeverity" value="CRITICAL" />

// Breeze score examples
<ChipColor type="breezeScore" value={2} />      // Low
<ChipColor type="breezeScore" value={4.5} />    // Medium
<ChipColor type="breezeScore" value={7} />      // High
<ChipColor type="breezeScore" value={9} />      // Critical

// Size variations
<ChipColor type="entity_risk" value={3} size="small" />
<ChipColor type="entity_risk" value={3} size="medium" />
<ChipColor type="entity_risk" value={3} size="large" />

// Other options
<ChipColor type="entity_risk" value={4} disabled />
<ChipColor type="entity_risk" value={4} width={120} labelWidth={true} />
<ChipColor type="entity_risk" value={4} defaultColor />`,
    demo: ChipColorDemo,
    category: "Display",
  },
  {
    id: "icon-chip",
    name: "IconChip",
    description:
      "A chip component that displays an icon with customizable styling and optional tooltip",
    props: [
      {
        name: "icon",
        type: "node",
        description: "The icon element to display in the chip",
      },
      {
        name: "bgcolor",
        type: "string",
        description: "Background color for the chip",
        defaultValue: "colors.chip_primary_enabled_bg",
      },
      {
        name: "shadowColor",
        type: "string",
        description: "Color for the box shadow",
        defaultValue: "colors.primary_48percent",
      },
      {
        name: "boxShadow",
        type: "string",
        description: "Custom box shadow styling",
        defaultValue: "0px 0px 1px 0px {shadowColor}",
      },
      {
        name: "tooltip",
        type: "string",
        description: "Optional tooltip text to display on hover",
        defaultValue: "null",
      },
      {
        name: "sx",
        type: "object",
        description: "Additional MUI system styling to apply to the chip",
        defaultValue: "{}",
      },
    ],
    example: `import IconChip from '../components/IconChip';
import SvgToIcon from '../icons/SvgToIcon';
import { severities, logos } from '../scenes/stack/environment/icons/iconExport';
import useColors from '../api/hooks/useColors';

const colors = useColors();

// Basic usage
<IconChip
  icon={
    <SvgToIcon
      IconLogo={severities.SeverityInfoIcon}
      width="20px"
      height="20px"
    />
  }
/>

// With custom background color
<IconChip
  icon={
    <SvgToIcon
      IconLogo={logos.GoogleIcon}
      width="20px"
      height="20px"
    />
  }
  bgcolor={colors.success_soft_label_fill}
/>

// With tooltip
<IconChip
  icon={
    <SvgToIcon
      IconLogo={logos.SlackIcon}
      width="20px"
      height="20px"
    />
  }
  tooltip="Slack"
/>

// With custom styling
<IconChip
  icon={
    <SvgToIcon
      IconLogo={logos.ZoomIcon}
      width="24px"
      height="24px"
    />
  }
  sx={{
    width: "40px",
    height: "40px",
  }}
/>

// With custom shadow
<IconChip
  icon={
    <SvgToIcon
      IconLogo={logos.SalesforceIcon}
      width="20px"
      height="20px"
    />
  }
  bgcolor={colors.primary_8percent}
  shadowColor={colors.primary_dark}
/>`,
    demo: IconChipDemo,
    category: "Display",
  },
  {
    id: "search-and-or-filter",
    name: "SearchAndOrFilter",
    description: "A filter menu with AND/OR logic and search capability.",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The display name for the filter.",
      },
      {
        name: "fieldName",
        type: "string",
        description: "The key in appliedFilters to control.",
      },
      {
        name: "options",
        type: "array",
        description: "Array of filter option objects.",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "The current filters state object.",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the filters state.",
      },
      {
        name: "filterLogic",
        type: "string",
        description: "Current logic (AND/OR).",
      },
      {
        name: "setFilterLogic",
        type: "function",
        description: "Function to update the logic.",
      },
    ],
    example: `import SearchAndOrFilter from '../components/filters/SearchAndOrFilter';
    import { useState } from 'react';

    const [appliedFilters, setAppliedFilters] = useState({ myField: [] });
    const [filterLogic, setFilterLogic] = useState('AND');

    <SearchAndOrFilter
      filterName="My Filter"
      fieldName="myField"
      options={[
        { name: "A", label: "Option A", displayName: "Option A" },
        { name: "B", label: "Option B", displayName: "Option B" },
        { name: "C", label: "Option C", displayName: "Option C" },
      ]}
      appliedFilters={appliedFilters}
      setAppliedFilters={setAppliedFilters}
      filterLogic={filterLogic}
      setFilterLogic={setFilterLogic}
    />`,
    demo: SearchAndOrFilterDemo,
    category: "Filters",
  },
  {
    id: "and-or-filter",
    name: "AndOrFilter",
    description: "A filter menu with AND/OR logic (no search).",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The display name for the filter.",
      },
      {
        name: "fieldName",
        type: "string",
        description: "The key in appliedFilters to control.",
      },
      {
        name: "options",
        type: "array",
        description: "Array of filter option objects.",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "The current filters state object.",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the filters state.",
      },
      {
        name: "setFilterLogic",
        type: "function",
        description: "Function to update the logic.",
      },
    ],
    example: `import AndOrFilter from '../components/filters/AndOrFilter';
    import { useState } from 'react';

    const [appliedFilters, setAppliedFilters] = useState({ myField: [] });
    const [filterLogic, setFilterLogic] = useState('OR');

    <AndOrFilter
      filterName="My Filter"
      fieldName="myField"
      options={[
        { name: "X", label: "Option X", displayName: "Option X" },
        { name: "Y", label: "Option Y", displayName: "Option Y" },
        { name: "Z", label: "Option Z", displayName: "Option Z" },
      ]}
      appliedFilters={appliedFilters}
      setAppliedFilters={setAppliedFilters}
      setFilterLogic={setFilterLogic}
    />`,
    demo: AndOrFilterDemo,
    category: "Filters",
  },
  {
    id: "time-filter",
    name: "TimeFilter",
    description:
      "A filter menu for selecting a time range (single select, with logic).",
    props: [
      {
        name: "filterName",
        type: "string",
        description: "The display name for the filter.",
      },
      {
        name: "fieldName",
        type: "string",
        description: "The key in appliedFilters to control.",
      },
      {
        name: "options",
        type: "array",
        description: "Array of time option objects.",
      },
      {
        name: "appliedFilters",
        type: "object",
        description: "The current filters state object.",
      },
      {
        name: "setAppliedFilters",
        type: "function",
        description: "Function to update the filters state.",
      },
      {
        name: "filterLogic",
        type: "string",
        description: "Current logic (AND/OR).",
      },
      {
        name: "setFilterLogic",
        type: "function",
        description: "Function to update the logic.",
      },
    ],
    example: `import TimeFilter from '../components/filters/TimeFilter';
import { useState } from 'react';

const timeOptions = [
  { name: "1h", label: "Last 1 hour", displayName: "Last 1 hour" },
  { name: "24h", label: "Last 24 hours", displayName: "Last 24 hours" },
  { name: "7d", label: "Last 7 days", displayName: "Last 7 days" },
  { name: "30d", label: "Last 30 days", displayName: "Last 30 days" },
];

const [appliedFilters, setAppliedFilters] = useState({ lastSeen: {} });
const [filterLogic, setFilterLogic] = useState({ lastSeen: "OR" });

<TimeFilter
  filterName="Last Seen"
  fieldName="lastSeen"
  options={timeOptions}
  appliedFilters={appliedFilters}
  setAppliedFilters={setAppliedFilters}
  filterLogic={filterLogic.lastSeen}
  setFilterLogic={logic => setFilterLogic({ ...filterLogic, lastSeen: logic })}
/>`,
    demo: TimeFilterDemo,
    category: "Filters",
  },
  {
    id: "filter-submenu",
    name: "FilterSubmenu",
    description:
      "A submenu for multi-select filtering, with icons and display names.",
    props: [
      {
        name: "anchorEl",
        type: "HTMLElement",
        description: "Anchor element for the menu.",
      },
      {
        name: "open",
        type: "boolean",
        description: "Whether the menu is open.",
      },
      {
        name: "onClose",
        type: "function",
        description: "Function to close the menu.",
      },
      { name: "title", type: "string", description: "Title of the submenu." },
      { name: "items", type: "array", description: "Array of item keys." },
      {
        name: "selectedItems",
        type: "array",
        description: "Array of selected item keys.",
      },
      {
        name: "onItemToggle",
        type: "function",
        description: "Function to toggle item selection.",
      },
      {
        name: "getIcon",
        type: "function",
        description: "Function to get icon for an item.",
      },
      {
        name: "getDisplayName",
        type: "function",
        description: "Function to get display name for an item.",
      },
      {
        name: "colors",
        type: "object",
        description: "Colors object for styling.",
      },
    ],
    example: `import FilterSubmenu from '.../FilterSubmenu';
import { useState } from 'react';
import { Button } from '@mui/material';

const tools = ["ToolA", "ToolB", "ToolC"];
const colors = {
  grey_48percent: "rgba(0,0,0,0.48)",
  text_secondary: "#888",
  text_primary: "#222",
};
const getIcon = () => ToolIcon;
const getDisplayName = (tool) => \`Display for \${tool}\`;

const [anchorEl, setAnchorEl] = useState(null);
const [selected, setSelected] = useState([]);

<Button onClick={e => setAnchorEl(e.currentTarget)}>Open</Button>
<FilterSubmenu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={() => setAnchorEl(null)}
  title="Select Tools"
  items={tools}
  selectedItems={selected}
  onItemToggle={item => setSelected(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item])}
  getIcon={getIcon}
  getDisplayName={getDisplayName}
  colors={colors}
/>`,
    demo: FilterSubmenuDemo,
    category: "Filters",
  },
  {
    id: "button-toggle-group",
    name: "ToggleButton",
    description:
      "A toggle button that is part of a group where only one button can be selected at a time.",
    props: [
      {
        name: "iconSVG",
        type: "ReactComponent",
        description:
          "An svg icon imported as a ReactComponent and color filled using currentColor.",
      },
      {
        name: "select",
        type: "boolean",
        description: "Whether the button is selected.",
      },
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
    ],
    example: `
import { ReactComponent as FirstIcon } from "../../.../icons/first_icon.svg";
import { ReactComponent as SecondIcon } from "../../.../icons/second_icon.svg";
import { ReactComponent as ThirdIcon } from "../../.../icons/third_icon.svg";

<ToggleGroup>
  <ToggleButton
    iconSVG={FirstIcon}
    selected={selected === option.FIRST}
    onClick={() => {
      setSelected(option.FIRST);
    }}
    disabled={false}
  >
    First
  </ToggleButton>
  <ToggleButton
    iconSVG={SecondIcon}
    selected={selected === option.SECOND}
    onClick={() => {
      setSelected(option.SECOND);
    }}
    disabled={false}
  >
    Second
  </ToggleButton>
    <ToggleButton
    iconSVG={ThirdIcon}
    selected={selected === option.THIRD}
    onClick={() => {
      setSelected(option.THIRD);
    }}
    disabled={true}
  >
    Disabled
  </ToggleButton>
</ToggleGroup>
    `,
    demo: ToggleGroupDemo,
    category: "Buttons",
  },
  {
    id: "button-contained-button",
    name: "ContainedButton",
    description: "A contained button aligned with our Figma design system.",
    props: [
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "color",
        type: "ReactComponent",
        description:
          "The name of the color out of inherit, primary, success, error (not all implemented yet).",
      },
      {
        name: "size",
        type: "boolean",
        description: "The size of the button large, medium, small, xs.",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
      {
        name: "loading",
        type: "boolean",
        description: "Whether the button is loading.",
      },
      {
        name: "children",
        type: "node",
        description: "The content of the button.",
      },
      {
        name: "sxProps",
        type: "object",
        description: "Additional styles to apply to the button.",
      },
    ],
    example: `
<ContainedButton
  onClick={handleClick}
  color="primary"
  size="small"
  loading={loading}
  disabled={disabled}
>
  Contained Button
</ContainedButton>
 `,
    demo: ContainedButtonDemo,
    category: "Buttons",
  },
  {
    id: "button-text-button",
    name: "TextButton",
    description: "A text button aligned with our Figma design system.",
    props: [
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "color",
        type: "string",
        description:
          "The name of the color out of inherit, primary, success, error.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the button large, medium, small, xs.",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
      {
        name: "loading",
        type: "boolean",
        description: "Whether the button is loading.",
      },
      {
        name: "children",
        type: "node",
        description: "The content of the button.",
      },
    ],
    example: `
<TextButton
  onClick={handleClick}
  color="inherit"
  size="small"
  loading={loading}
  disabled={disabled}
>
  Text Button
</TextButton>
 `,
    demo: TextButtonDemo,
    category: "Buttons",
  },
  {
    id: "button-outlined-button",
    name: "OutlinedButton",
    description: "An outlined button aligned with our Figma design system.",
    props: [
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "color",
        type: "string",
        description:
          "The name of the color out of inherit, primary, success, error.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the button large, medium, small, xs.",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
      {
        name: "loading",
        type: "boolean",
        description: "Whether the button is loading.",
      },
      {
        name: "icon",
        type: "ReactComponent",
        description:
          "An optional svg as a ReactComponent. If the svg is currentColor, the button color will determine the icon color when enabled/disabled. If the svg is colored, a disabledIcon must be provided as well.",
      },
      {
        name: "iconSize",
        type: "int",
        description: "Override the default icon size.",
      },
      {
        name: "disabledIcon",
        type: "ReactComponent",
        description:
          "A svg as a ReactComponent. This icon is used when the button is disabled.",
      },
      {
        name: "startIcon",
        type: "svg",
        description: "An optional currentColor svg as a ReactComponent.",
      },
      {
        name: "endIcon",
        type: "svg",
        description: "An optional currentColor svg as a ReactComponent.",
      },
      {
        name: "children",
        type: "node",
        description: "Text content only for the button. Don't use a component.",
      },
    ],
    example: `
import { ReactComponent as myIconSVG } from "../icons/my_icon.svg";
import { ReactComponent as myColoredIconSVG } from "../icons/my_colored_icon.svg";
import { ReactComponent as myDisabledColoredIconSVG } from "../icons/my_disabled_colored_icon.svg";
<OutlinedButton
  onClick={() => {}}
  color="inherit"
  size="small"
  icon={myIconSVG}
/>
<OutlinedButton
  onClick={() => {}}
  color="inherit"
  size="small"
>
  My button
</OutlinedButton>
<OutlinedButton
  onClick={() => {}}
  color="inherit"
  size="small"
  icon={myIconSVG}
>
  My button
</OutlinedButton>
<OutlinedButton
  onClick={() => {}}
  color="inherit"
  size="small"
  icon={myColoredIconSVG}
  disabledIcon={myDisabledColoredIconSVG}
>
  My button
</OutlinedButton>
 `,
    demo: OutlinedButtonDemo,
    category: "Buttons",
  },
  {
    id: "button-icon-button",
    name: "IconButton",
    description: "An icon only button aligned with our Figma design system.",
    props: [
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "IconSVG",
        type: "ReactComponent",
        description:
          "A currentColor svg as a ReactComponent. Currently not supporting colored icons.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the button XS, S, M, L.",
      },
      {
        name: "buttonStyle",
        type: "string",
        description: "The type of button. outlined or standard",
      },
      {
        name: "color",
        type: "string",
        description:
          "The name of the color out of inherit and primary (not all implemented yet).",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
      {
        name: "loading",
        type: "boolean",
        description: "Whether the button is loading.",
      },
    ],
    example: `
import IconButton from "../../components/buttons/IconButton";
import { ReactComponent as PDFIconSVG } from "../../scenes/stack/environment/icons/Export_PDF_currentColor.svg";

<IconButton
  onClick={handleClick}
  IconSVG={PDFIconSVG}
  size="S"
  buttonStyle="outlined"
  color="inherit"
  disabled={disabled}
  loading={loading}
/>
 `,
    demo: IconButtonDemo,
    category: "Buttons",
  },
  {
    id: "button-icon-container",
    name: "IconContainer",
    description: "An icon container aligned with our Figma design system.",
    props: [
      {
        name: "IconSVG",
        type: "ReactComponent",
        description: "A svg as a ReactComponent.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the container XS, S, M, L.",
      },
    ],
    example: `
import IconContainer from "../../components/IconContainer";
import { ReactComponent as BreezeSVG } from "../../scenes/stack/environment/icons/logos/breeze-24.svg";
    
<IconContainer IconSVG={BreezeSVG} size="M" />
    `,
    demo: IconContainerDemo,
    category: "Display",
  },
  {
    id: "button-soft-button",
    name: "SoftButton",
    description: "A soft button aligned with our Figma design system.",
    props: [
      {
        name: "onClick",
        type: "function",
        description: "Function to be called when the button is clicked.",
      },
      {
        name: "color",
        type: "string",
        description:
          "The name of the color out of inherit, primary, success, error.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the button large, medium, small, xs.",
      },
      {
        name: "disabled",
        type: "boolean",
        description: "Whether the button is disabled.",
      },
      {
        name: "loading",
        type: "boolean",
        description: "Whether the button is loading.",
      },
      {
        name: "icon",
        type: "ReactComponent",
        description:
          "An optional svg as a ReactComponent. If the svg is currentColor, the button color will determine the icon color when enabled/disabled. If the svg is colored, a disabledIcon must be provided as well.",
      },
      {
        name: "disabledIcon",
        type: "ReactComponent",
        description:
          "A svg as a ReactComponent. This icon is used when the button is disabled.",
      },
      {
        name: "children",
        type: "node",
        description: "Text content only for the button. Don't use a component.",
      },
    ],
    example: `
import SoftButton from "../../components/buttons/SoftButton";
import { ReactComponent as FlashIcon } from "../../icons/icon_files/flash.svg";

<SoftButton
  onClick={handleClick}
  size="small"
  color="primary"
  icon={IconSVG}
>
  My Soft Button
</SoftButton>
 `,
    demo: SoftButtonDemo,
    category: "Buttons",
  },
  {
    id: "tags-tag-input",
    name: "TagInput",
    description: "A tag input aligned with our Figma design system.",
    props: [
      {
        name: "tagStyle",
        type: "string",
        description: "The style of the tag input: filled, outlined, rectangle.",
      },
      {
        name: "color",
        type: "string",
        description:
          "The color of the tag input: inherit, error, success, warning, overlay.",
      },
      {
        name: "size",
        type: "string",
        description: "The size of the tag input: S, M, L.",
      },
      {
        name: "children",
        type: "node",
        description: "Text content for the tag input.",
      },
    ],
    example: `
<TagInput tagStyle="filled" color="overlay" size="M">
    Overlay Medium
</TagInput>
 `,
    demo: TagInputDemo,
    category: "Tags",
  },
];

export default componentsData;
